import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { ApolloError } from '@apollo/client/errors';
import { NetworkStatus, Observable, fromPromise } from '@apollo/client/core';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { NavigateFunction } from 'react-router';

import { config } from '@/config';
import { Lng } from '@/i18n/resources';
import { SnackbarContent, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import i18n from '@/i18n/i18n';
import { getErrorResponseMessages, getTranslatedErrorMessages } from '@/utils/gqlErrors';
import { ROUTE_PATH } from '@/constants/routePaths';

export interface HookResult {
  error?: ApolloError;
  loading: boolean;
  networkStatus: NetworkStatus;
  refetch: (variables?: Partial<Record<string, any>>) => Promise<ApolloQueryResult<any>>;
}

const customFetch = (input: RequestInfo | URL, options?: RequestInit) => {
  let operationName = 'unknown';
  try {
    if (typeof options?.body === 'string') {
      const parsedOptions = JSON.parse(options?.body as string);
      if (Array.isArray(parsedOptions)) {
        operationName = parsedOptions.map((option: any) => option.operationName).join(',');
      } else {
        operationName = parsedOptions.operationName;
      }
    } else if (options?.body instanceof FormData) {
      operationName = JSON.parse(options.body.get('operations') as string).operationName;
    }
  } catch (error) {
    console.error(error, options?.body);
  }

  return fetch(`${config.frontendApiRoute}/graphql?opname=${operationName}`, {
    ...options,
    headers: {
      ...options?.headers,
      /**
       * apollo-require-preflight is required to work around csrfPrevention.
       *
       * @see https://github.com/apollographql/apollo-server/issues/6433
       */
      'apollo-require-preflight': 'true',
      'accept-language': localStorage.getItem('i18nextLng') || Lng.en,
    },
  });
};

const httpLink = createUploadLink({ fetch: customFetch, credentials: 'include' });

interface LinkOptions {
  openSnackbar: (param: SnackbarContent) => void;
}

const IGNORED_QUERY_ERRORS = ['providerAccessProductId', 'issuedLicense', 'learningById', 'learningTypeById'];
const PUBLIC_ROUTES = [ROUTE_PATH.LOGIN, ROUTE_PATH.INJECT_REFLECTION, ROUTE_PATH.TERMS, ROUTE_PATH.PRIVACY, ROUTE_PATH.ABOUT];

function isAuthFlowRoute(path: string): boolean {
  for (const route of PUBLIC_ROUTES) {
    if (path.indexOf(route) === 0) return true;
  }
  return false;
}

const removeTypenameLink = removeTypenameFromVariables();

/** @returns whether the failed operation should be retried (refresh succeeded). */
const unauthenticatedResponseHandler = async (query: string, navigate: NavigateFunction): Promise<boolean> => {
  try {
    // TODO: remove when authv2 is a required config option
    if (!config.authv2) throw new Error('Authv2 config is missing.');

    // TODO: Currently, concurrent queries cause multiple refresh
    // requests. It would be nice to only having to request once.
    const response = await fetch(config.authv2.refresh_url, {
      credentials: 'include',
    });

    if (response.status !== 200) {
      throw new Error('Token refresh failed. ');
    }
    return true;
  } catch {
    if (location.pathname === ROUTE_PATH.LOGIN) {
      return false;
    }
    if (!(query === 'me' && (isAuthFlowRoute(location.pathname) || location.pathname === '/'))) {
      /**
       * Do not call clearStore/refetchQueries here. This handler runs from the GraphQL error link while the
       * `me` watchQuery is still tied to the errored operation; clearing the store leaves that observable
       * stuck with loading=true (no follow-up request), which matches what we see in DevTools: one `me`
       * response, failed refresh, then only realm refetches. LoginPage immediately hard-redirects to auth,
       * so we only soft-navigate to /login with returnUrl and let the error propagate (see UNAUTHENTICATED branch).
       */
      navigate({
        pathname: ROUTE_PATH.LOGIN,
        search: `?${new URLSearchParams({
          returnUrl: `${location.origin}${location.pathname}${location.search}`,
        }).toString()}`,
      });
    }
    return false;
  }
};

const createApolloClient = ({ openSnackbar }: LinkOptions, navigate: NavigateFunction): ApolloClient<NormalizedCacheObject> => {
  const apolloClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    connectToDevTools: true,
    link: ApolloLink.from([
      removeTypenameLink,
      onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (graphQLErrors) {
          const code = graphQLErrors[0].extensions?.code;
          const query = operation.operationName;
          console.warn('GraphQLErrors', graphQLErrors);
          // display no errors for discussions reply navigations
          if (query === 'getReplies' && graphQLErrors?.[0]?.message === '404: Not Found') return;
          // display no errors for injected reflections
          if (query === 'getPost' && location.pathname === '/inject-reflection') return;
          if (code === 'UNAUTHENTICATED') {
            return fromPromise(unauthenticatedResponseHandler(query, navigate)).flatMap((shouldRetry) => {
              if (shouldRetry) {
                return forward(operation);
              }
              // Refresh failed: retrying keeps the watchQuery stuck in loading and can loop forever.
              return new Observable((observer) => {
                observer.error(
                  new ApolloError({
                    graphQLErrors,
                  }),
                );
              });
            });
          } else if (!IGNORED_QUERY_ERRORS.includes(query)) {
            const [firstTrans] = getTranslatedErrorMessages(graphQLErrors);
            const [firstErr, ...messages] = getErrorResponseMessages(graphQLErrors);

            const defaultSnackbarMessage = i18n.t('serverMessages::generic|:error-occurred', {
              message: firstTrans || firstErr,
              count: messages.length,
            });

            const contactSupportSnackbarMessage = i18n.t('serverMessages::generic|:error-occured-contact-support');

            openSnackbar({
              message:
                firstErr === 'generic|:error-occured-contact-support' ? contactSupportSnackbarMessage : defaultSnackbarMessage,
              type: SnackbarType.DANGER,
            });
          }
        }
        if (networkError) {
          console.error('NetworkError', networkError);
        }
      }),
      httpLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        UserMeta: {
          keyFields: ['userId', 'type'],
          fields: {
            meta: {
              merge: false,
            },
          },
        },
        InventoryProduct: {
          keyFields: ['id', 'provider'],
        },
        InventoryItem: {
          // PT-2080: Must use "productId" to cache inventory items. Id is always random
          keyFields: ['id', 'productId'],
        },
        AssignmentStrategicV3User: {
          keyFields: ['userId', 'assignmentId'],
        },
        AssignmentGroup: {
          fields: {
            users: {
              merge: false,
            },
          },
        },
        AssignmentGroupUser: {
          keyFields: ['id', 'assignmentGroupId'],
        },
      },
    }),
  });
  apolloClient.defaultOptions = {
    watchQuery: {
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  };
  return apolloClient;
};

export { createApolloClient };
